import { useMutation, useQuery } from "@apollo/client";
import { Divider, GridLegacy as Grid, Stack, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EditPerson } from "components/Contacts/EditPerson";
import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import {
  ContactReportRole,
  GetPersonForEditQuery,
  Permission,
  PersonInput,
} from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacilityContact";
import { useAuthorization } from "hooks/useAuthorization";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useWatch } from "react-hook-form";
import { usePersonFacilityContactsInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/usePersonFacilityContactsInputValidation";
import { hasCriticalIssues } from "util/forms";

import AssociatedFacilities from "./AssociatedFacilities";
import { ConfirmPersonChangesModal } from "./ConfirmPersonChangesModal";

const GET_PERSON = gql(`
  query GetPersonForEdit($id: ID!) {
    person(id: $id) {
      id
      tenantId
      first
      last
      phones {
        number
        type
      }
      renderPhone {
        inputMask
      }
      email
      title
      company
    
      attentionTo
      streetAddress1
      streetAddress2
      city
      country
      state
      zip

      isEncamp

      tankOwnerType

      dbNumber

      facilityContacts {
        id
        reportingRoles
        facility {
          id
          name
          streetAddress1
          streetAddress2
          city
          state
          zip
          country
        }
      }
      user {
        id
        email
      }
      issues {
        ...issue
      }
    }
  }
`);

const UPSERT_PERSON = gql(`
  mutation UpsertPerson($input: PersonFacilityContactsInput!) {
    upsertPerson(input: $input) {
      id
    }
  }
`);

export type EditContactFormData = {
  person?: GetPersonForEditQuery["person"];
};

export const EditContactDialog = ({
  open,
  onClose,
  onSave,
  personId,
  refetchQueries,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (person: PersonInput | undefined) => void;
  personId?: string;
  refetchQueries?: string[];
}) => {
  const alerts = useAlerts();
  const { hasPermissions } = useAuthorization();
  const canWrite = hasPermissions([Permission.WritePerson]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const theme = useTheme();
  const { tenantId } = useTenant();

  const { data: queryData, loading } = useQuery(GET_PERSON, {
    variables: { id: personId ?? "" },
    skip: !personId,
    fetchPolicy: "cache-and-network",
  });
  const person = queryData?.person ?? undefined;

  const defaultValues: EditContactFormData | undefined = useMemo(
    () => ({
      id: person?.id ?? "",
      person: {
        ...person,
        tenantId: person?.tenantId ?? tenantId ?? "",
        id: person?.id ?? "",
      },
      facilityContacts: person?.facilityContacts,
    }),
    [person, tenantId]
  );

  const [upsertPerson, { loading: savingPerson }] = useMutation(UPSERT_PERSON, {
    refetchQueries: [...(refetchQueries ?? []), GET_PERSON],
  });

  const form = useValidatingForm<EditContactFormData>(
    defaultValues,
    person?.issues ?? [],
    usePersonFacilityContactsInputValidation()
  );

  const {
    handleSubmit,
    issues,
    control,
    formState: { isDirty },
  } = form;

  // Used to update facilityContact records
  const { remove, replace } = useFieldArray({
    control,
    name: "person.facilityContacts",
  });

  const editingMode = useMemo(() => {
    if (person?.isEncamp || !canWrite) {
      return "displayOnly";
    }
    return personId ? "edit" : "add";
  }, [personId, person?.isEncamp, canWrite]);

  const dialogTitle = useMemo(() => {
    if (editingMode === "add") return "Add Contact";
    if (editingMode === "displayOnly") return "View Contact";
    return "Edit Contact";
  }, [editingMode]);

  const upsert = useCallback(
    async (data: EditContactFormData) => {
      if (!data.person) {
        return;
      }
      if (!tenantId) {
        throw new Error("Tenant ID is required");
      }
      const input = transform(data);
      const result = await upsertPerson({ variables: { input } });
      if (editingMode === "add") {
        // call onSave including the new id
        const id = result.data?.upsertPerson?.id;
        onSave({ ...data.person, id });
      } else {
        onSave(data.person);
      }
      return result.data?.upsertPerson?.id;
    },
    [upsertPerson, onSave, tenantId, editingMode]
  );

  const onSubmit = useCallback(
    async (data: EditContactFormData) => {
      const updatePerson = data.person;
      if (!updatePerson) {
        return;
      }
      if (updatePerson?.id?.length && updatePerson?.facilityContacts?.length && isDirty) {
        setShowConfirmModal(true);
      } else {
        try {
          await upsert(data);
          onClose();
          alerts.success("Contact added successfully");
        } catch (error) {
          alerts.error("Error adding contact", error);
        }
      }
    },
    [isDirty, upsert, alerts, onClose]
  );

  const watchedFacilityContacts = useWatch({
    name: "person.facilityContacts",
    control,
  });

  return (
    <Dialog
      open={open}
      title={dialogTitle}
      onClose={() => {
        onClose();
      }}
      maxWidth="xl"
      noPrimaryAction={editingMode === "displayOnly"}
      primaryActionButtonProps={{
        disabled: hasCriticalIssues(issues) || !isDirty,
        loading: savingPerson,
        onClick: () => {
          handleSubmit(onSubmit)();
        },
      }}
      additionalPrimaryActionElements={[
        <IssueListButton issues={issues} key="issue-list-button" />,
      ]}
    >
      <form>
        {editingMode === "edit" && person?.user ? (
          <Typography variant="body1" sx={{ mb: theme.spacing(3) }}>
            You cannot update this contact's email or name because it is associated with a specific
            person in your organization. If you'd like a contact with a different email please
            create a new contact. Reach out to your Encamp Customer Service Manager if you have
            additional questions.
          </Typography>
        ) : null}

        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          spacing={theme.spacing(3)}
        >
          <Grid container spacing={theme.spacing(3)} width="60%">
            <Grid item xs={12}>
              <FormProvider {...form}>
                <EditPerson
                  mode={editingMode}
                  isUser={!!person?.user}
                  loading={loading}
                  issues={issues}
                  showTankOwnerType={
                    form
                      .getValues()
                      .person?.facilityContacts?.some((fc) =>
                        fc.reportingRoles.some((rr) => rr === ContactReportRole.EpcraCaTankOwner)
                      ) ?? false
                  }
                  showDunAndBradstreetNumber={form
                    .getValues()
                    .person?.facilityContacts?.some((fc) =>
                      fc.reportingRoles.some((rr) => rr === ContactReportRole.EpcraT2MParentCompany)
                    )}
                />
              </FormProvider>
            </Grid>
          </Grid>
          <Stack width="40%">
            <AssociatedFacilities
              facilityContacts={watchedFacilityContacts ?? []}
              onUpdate={(updates) => replace(updates)}
              onRemove={(index) => remove(index)}
              readOnly={editingMode === "displayOnly"}
            />
          </Stack>
        </Stack>
      </form>
      {showConfirmModal && (
        <ConfirmPersonChangesModal
          personFullName={getFullName(person)}
          facilities={watchedFacilityContacts?.map((fc) => fc.facility) ?? []}
          saving={savingPerson}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleSubmit(async (data) => {
            try {
              await upsert(data);
              setShowConfirmModal(false);
              alerts.success("Contact updated successfully");
            } catch (error) {
              alerts.error("Error updating contact", error);
            }
          })}
        />
      )}
    </Dialog>
  );
};
